import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  fetchYowanRelatedSeminarApplicationList,
  fetchYowanRelatedSeminarDetail,
} from '../app/yowanContentClient';
import {
  getStakeholderMessagesHref,
  getStakeholderSeminarHref,
  StakeholderGuardNotice,
  StakeholderShell,
  StakeholderSearchToolbar,
  useStakeholderGuard,
} from './StakeholderSeminarsPage';
import {
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
const { yowanPageContent } = require('./pageContent');

const applicationSearchText = (row = {}) => [
  row.applicantName,
  row.applicantNameKana,
  row.applicantEmail,
  row.participationScopeLabel,
  row.slotParticipationLabel,
  row.statusLabel,
].filter(Boolean).join(' ').toLowerCase();

export const useStakeholderSeminarApplications = ({ eventId, guardStatus, loadErrorMessage }) => {
  const [seminar, setSeminar] = useState(null);
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (guardStatus !== 'ready') {
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    Promise.all([
      fetchYowanRelatedSeminarDetail(eventId).catch(() => null),
      fetchYowanRelatedSeminarApplicationList({ seminarId: eventId }),
    ])
      .then(([seminarPayload, applicationPayload]) => {
        if (!isMounted) {
          return;
        }

        setSeminar(applicationPayload.seminar || seminarPayload);
        setRecords(applicationPayload.results || []);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setSeminar(null);
        setRecords([]);
        setLoadError(loadErrorMessage || '申込一覧を読み込めませんでした。');
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [eventId, guardStatus, loadErrorMessage]);

  return {
    seminar,
    records,
    isLoading,
    loadError,
  };
};

const StakeholderSeminarApplicationsPage = ({ guardOptions }) => {
  const { eventId } = useParams();
  const content = yowanPageContent['/stakeholder/seminars/:eventId/applications'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const guard = useStakeholderGuard(guardOptions);
  const [searchQuery, setSearchQuery] = useState('');
  const {
    seminar,
    records,
    isLoading,
    loadError,
  } = useStakeholderSeminarApplications({
    eventId,
    guardStatus: guard.status,
    loadErrorMessage: content?.loadErrorMessage,
  });

  const visibleRecords = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    if (!normalizedQuery) {
      return records;
    }

    return records.filter((row) => applicationSearchText(row).includes(normalizedQuery));
  }, [records, searchQuery]);

  if (guard.status !== 'ready') {
    return <StakeholderGuardNotice notice={guard.notice} />;
  }

  return (
    <StakeholderShell
      activeNavHref="/stakeholder/seminars"
      routeKey="/stakeholder/seminars/:eventId/applications"
      currentUser={guard.currentUser}
    >
      <AdminPageHeader
        title={seminar ? `${seminar.title} 申込一覧` : content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="detail" to={getStakeholderSeminarHref(eventId, preservedDomainSearch)}>
            詳細
          </Link>,
          <Link key="messages" to={getStakeholderMessagesHref(eventId, preservedDomainSearch)}>
            申込メッセージ
          </Link>,
        ]}
      />

      <StakeholderSearchToolbar
        searchPlaceholder={content.searchPlaceholder}
        searchValue={searchQuery}
        onSearchChange={(event) => setSearchQuery(event.target.value)}
        metaText={`${visibleRecords.length}件表示`}
      />

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        {isLoading ? <LoadingSpinner label="申込一覧を読み込み中" showLabel /> : null}
        {!isLoading && loadError ? <p className="yowan-admin-form__message">{loadError}</p> : null}
        {!isLoading && !loadError && visibleRecords.length === 0 ? (
          <p className="yowan-admin-form__message">表示できる申込はありません。</p>
        ) : null}
        {!isLoading && !loadError && visibleRecords.length > 0 ? (
          <table className="yowan-admin-resource-table yowan-stakeholder-table yowan-stakeholder-table--seminar-applications">
            <thead>
              <tr>
                <th>申込者</th>
                <th>参加対象</th>
                <th>参加費</th>
                <th>ステータス</th>
                <th>申込日時</th>
              </tr>
            </thead>
            <tbody>
              {visibleRecords.map((row) => (
                <tr key={row.id}>
                  <td>
                    <p className="yowan-admin-resource-table__title">{row.applicantName || '-'}</p>
                    {row.applicantNameKana ? <p className="yowan-admin-resource-table__subline">{row.applicantNameKana}</p> : null}
                    {row.applicantEmail ? <p className="yowan-admin-resource-table__subline">{row.applicantEmail}</p> : null}
                  </td>
                  <td>{row.slotParticipationLabel || row.participationScopeLabel || '-'}</td>
                  <td>{row.slotFeeLabel || '-'}</td>
                  <td>{row.statusLabel || '-'}</td>
                  <td>{row.createdAt || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </StakeholderShell>
  );
};

export default StakeholderSeminarApplicationsPage;
